/**
 * The manual timer, for calls and work away from CaseBlink. Stopping it
 * becomes a manual entry on the case it was started on.
 */
import { Play, Square, X } from "lucide-react";
import { useEffect, useState, type FC } from "react";

import { Button } from "@/components/ui/button";

import { timeActions } from "../actions";
import type { RunningTimer } from "../types";
import { formatDuration } from "../utils";

/** Seconds since the timer started, refreshed every 15 seconds. */
export const useTimerElapsed = (timer: RunningTimer | null): number => {
  const [, tick] = useState(0);
  useEffect(() => {
    if (!timer) return undefined;
    const t = window.setInterval(() => tick((n) => n + 1), 15_000);
    return () => window.clearInterval(t);
  }, [timer]);
  return timer
    ? Math.max(0, (Date.now() - new Date(timer.started_at).getTime()) / 1000)
    : 0;
};

export const TimerButton: FC<{
  timer: RunningTimer | null;
  caseId?: string | null;
}> = ({ timer, caseId }) => {
  const elapsed = useTimerElapsed(timer);
  if (!timer) {
    return (
      <Button
        variant="secondary"
        size="sm"
        onClick={() => void timeActions.startTimer(caseId)}
        data-testid="timer-start"
      >
        <Play /> Timer
      </Button>
    );
  }
  return (
    <span
      className="inline-flex items-center gap-1"
      data-testid="timer-running"
    >
      <Button
        variant="secondary"
        size="sm"
        aria-label="Stop the timer"
        onClick={() => void timeActions.stopTimer()}
      >
        <Square className="fill-danger text-danger" />
        <span className="tabular-nums">{formatDuration(elapsed)}</span>
      </Button>
      <Button
        variant="ghost"
        size="icon-sm"
        aria-label="Discard the timer"
        onClick={() => void timeActions.stopTimer(true)}
      >
        <X />
      </Button>
    </span>
  );
};
